import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck,
  UploadSimple,
  Trash,
  CheckCircle,
  XCircle,
  CaretDown,
  Clock,
  Globe,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  deleteDmarcReport,
  getDmarcReport,
  listDmarcReports,
  uploadDmarcReport,
} from "../lib/api";

function fmtDate(unix) {
  if (!unix) return "—";
  const d = new Date(unix * 1000);
  return d.toISOString().slice(0, 10);
}

function ReportRow({ item, expanded, onToggle, onDelete }) {
  const pct = item.pass_rate ?? 0;
  const color = pct >= 95 ? "#4ade80" : pct >= 80 ? "#facc15" : "#f87171";
  return (
    <>
      <tr
        className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
        onClick={onToggle}
        data-testid={`dmarc-row-${item.id}`}
      >
        <td className="p-3">
          <div className="text-sm text-zinc-200">{item.org_name}</div>
          <div className="text-[10px] text-zinc-500 font-mono mt-1">{item.org_email}</div>
        </td>
        <td className="p-3 text-xs text-zinc-400 font-mono">
          {fmtDate(item.date_begin)} → {fmtDate(item.date_end)}
        </td>
        <td className="p-3 text-right font-mono text-sm text-zinc-200">
          {item.total_messages?.toLocaleString() ?? 0}
        </td>
        <td className="p-3 text-right">
          <span
            className="inline-block px-2 py-1 font-mono text-xs font-bold"
            style={{ color, background: `${color}1a`, border: `1px solid ${color}55` }}
          >
            {pct.toFixed(1)}%
          </span>
        </td>
        <td className="p-3 text-right">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            data-testid={`dmarc-delete-${item.id}`}
            className="p-1.5 text-zinc-500 hover:text-red-400"
          >
            <Trash size={13} />
          </button>
        </td>
        <td className="p-3 text-right">
          <CaretDown
            size={14}
            className={`text-zinc-500 transition-transform ${expanded ? "rotate-180 text-[#ccff00]" : ""}`}
          />
        </td>
      </tr>
      {expanded && <DmarcDetail reportId={item.id} />}
    </>
  );
}

function DmarcDetail({ reportId }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    getDmarcReport(reportId).then(setDetail).catch(() => setDetail(null));
  }, [reportId]);
  if (!detail) return null;
  return (
    <tr className="bg-[#080808] border-b border-white/5">
      <td colSpan={6} className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="label-mono mb-2">policy published</div>
            <dl className="grid grid-cols-2 gap-y-1 text-xs font-mono">
              <dt className="text-zinc-500">domain</dt>
              <dd className="text-zinc-200">{detail.policy_domain}</dd>
              <dt className="text-zinc-500">policy</dt>
              <dd className="text-zinc-200">{detail.policy_p} ({detail.policy_pct}%)</dd>
              <dt className="text-zinc-500">report id</dt>
              <dd className="text-zinc-200 truncate">{detail.report_id}</dd>
              <dt className="text-zinc-500">pass / fail</dt>
              <dd>
                <span className="text-emerald-400">{detail.pass_count}</span>
                <span className="text-zinc-500 mx-1">/</span>
                <span className="text-red-400">{detail.fail_count}</span>
              </dd>
            </dl>

            <div className="label-mono mt-4 mb-2">top source IPs</div>
            <div className="space-y-1">
              {(detail.top_sources || []).map(([ip, count]) => (
                <div key={ip} className="flex items-center gap-2 text-xs font-mono">
                  <Globe size={11} className="text-zinc-500" />
                  <span className="text-zinc-300">{ip}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-400">{count} msgs</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">records ({detail.records?.length ?? 0})</div>
            <div className="max-h-64 overflow-y-auto border border-white/5">
              <table className="w-full text-left">
                <thead className="bg-[#050505] sticky top-0">
                  <tr>
                    <th className="p-2 label-mono">source</th>
                    <th className="p-2 label-mono text-right">msgs</th>
                    <th className="p-2 label-mono text-center">dkim</th>
                    <th className="p-2 label-mono text-center">spf</th>
                    <th className="p-2 label-mono text-center">disp.</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.records || []).map((r) => (
                    <tr
                      key={`${r.source_ip}-${r.header_from}-${r.dkim_aligned}-${r.spf_aligned}-${r.count}`}
                      className="border-b border-white/5 text-xs font-mono"
                    >
                      <td className="p-2 text-zinc-300 truncate max-w-[140px]">{r.source_ip}</td>
                      <td className="p-2 text-right text-zinc-200">{r.count}</td>
                      <td className="p-2 text-center">
                        {r.dkim_aligned === "pass" ? (
                          <CheckCircle size={12} weight="fill" className="text-emerald-400 inline" />
                        ) : (
                          <XCircle size={12} weight="fill" className="text-red-400 inline" />
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {r.spf_aligned === "pass" ? (
                          <CheckCircle size={12} weight="fill" className="text-emerald-400 inline" />
                        ) : (
                          <XCircle size={12} weight="fill" className="text-red-400 inline" />
                        )}
                      </td>
                      <td className="p-2 text-center text-zinc-400">{r.disposition}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function Dmarc() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setData(await listDmarcReports());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load reports");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let inserted = 0;
    let dupes = 0;
    for (const file of files) {
      try {
        const res = await uploadDmarcReport(file);
        inserted += res.inserted.length;
        dupes += res.skipped_duplicates;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (inserted > 0 || dupes > 0) {
      toast.success(`Ingested ${inserted} report(s)${dupes ? ` · skipped ${dupes} duplicate(s)` : ""}`);
      refresh();
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const items = data?.items || [];
  const pct = data?.overall_pass_rate ?? 0;
  const color = pct >= 95 ? "#4ade80" : pct >= 80 ? "#facc15" : "#f87171";

  return (
    <div className="max-w-6xl">
      <div className="mb-10">
        <div className="label-mono mb-3">domain auth reports</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tighter">
          <span className="font-black text-[#ccff00]">DMARC</span> Reports
        </h1>
        <p className="text-zinc-400 text-sm mt-3 max-w-2xl">
          Weekly aggregate reports from Google, Microsoft, Yahoo & other providers. Forward them to
          your inbox, then drag the <code>.xml</code>, <code>.xml.gz</code>, or <code>.zip</code>{" "}
          attachment here — we&apos;ll parse and visualize pass/fail rates + spoof attempts.
        </p>
      </div>

      {/* Stats */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="dmarc-stats">
          <div className="bg-[#0a0a0a] border border-white/10 p-4">
            <div className="label-mono mb-1">reports</div>
            <div className="font-display text-3xl font-black text-white">
              {data?.total_reports ?? 0}
            </div>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 p-4">
            <div className="label-mono mb-1">total messages</div>
            <div className="font-display text-3xl font-black text-white">
              {(data?.total_messages ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 p-4">
            <div className="label-mono mb-1">passed</div>
            <div className="font-display text-3xl font-black text-emerald-400">
              {(data?.total_pass ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-[#0a0a0a] border border-white/10 p-4">
            <div className="label-mono mb-1">overall pass rate</div>
            <div className="font-display text-3xl font-black" style={{ color }}>
              {pct.toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      {/* Upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed p-8 mb-6 text-center transition-colors ${
          dragOver
            ? "border-[#ccff00] bg-[#ccff00]/5"
            : "border-white/10 hover:border-white/30 bg-[#0a0a0a]"
        }`}
        data-testid="dmarc-upload-zone"
      >
        <UploadSimple size={28} weight="duotone" className="text-zinc-500 mx-auto mb-3" />
        <div className="text-sm text-zinc-300 mb-1">
          {uploading ? "Uploading & parsing…" : "Drop DMARC report file(s) here"}
        </div>
        <div className="text-[11px] text-zinc-500 font-mono">
          .xml · .xml.gz · .zip · max 5MB per file
        </div>
        <label className="inline-block mt-4">
          <input
            type="file"
            multiple
            accept=".xml,.gz,.zip"
            data-testid="dmarc-file-input"
            onChange={(e) => handleFiles(Array.from(e.target.files))}
            className="hidden"
          />
          <span className="inline-block cursor-pointer bg-[#ccff00] text-black font-semibold px-5 py-2 text-sm hover:bg-[#b3e600] transition-colors">
            or choose files
          </span>
        </label>
      </div>

      {/* Reports table */}
      <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden" data-testid="dmarc-table">
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-white/10">
            <tr>
              <th className="p-3 label-mono">reporter</th>
              <th className="p-3 label-mono">date range</th>
              <th className="p-3 label-mono text-right">messages</th>
              <th className="p-3 label-mono text-right">pass rate</th>
              <th className="p-3 w-8"></th>
              <th className="p-3 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-12 text-center">
                  <ShieldCheck size={32} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
                  <div className="text-zinc-400 text-sm max-w-md mx-auto">
                    No reports uploaded yet. DMARC reports usually arrive within 24–48 hours of your
                    DMARC record going live. When they hit your inbox, download the attachment and
                    drop it above.
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-3">
                    common senders: <code>noreply-dmarc-support@google.com</code>,{" "}
                    <code>dmarcreport@microsoft.com</code>
                  </div>
                </td>
              </tr>
            )}
            {items.map((item) => (
              <ReportRow
                key={item.id}
                item={item}
                expanded={!!expanded[item.id]}
                onToggle={() =>
                  setExpanded((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                }
                onDelete={async () => {
                  await deleteDmarcReport(item.id);
                  toast.success("Report deleted");
                  refresh();
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Tip */}
      <div className="mt-6 border border-white/5 bg-[#0a0a0a] p-4 flex items-start gap-3">
        <Clock size={16} className="text-[#ccff00] mt-0.5 flex-shrink-0" />
        <div className="text-xs text-zinc-400 font-mono leading-relaxed">
          <b>Tip:</b> once you have 2–4 weeks of reports showing <span className="text-emerald-400">100% pass rate</span> across all sources,
          tighten your DMARC policy from <code className="text-white">p=none</code> to{" "}
          <code className="text-white">p=quarantine</code> (spoofed mail goes to spam) and
          eventually <code className="text-white">p=reject</code> (spoofed mail is bounced).
        </div>
      </div>
    </div>
  );
}
